/**
 * Trailing-edge debounce. Returns a callable that delays invoking `fn` until
 * `wait` ms have passed since the last call. Exposes:
 *   - flush():  immediately invoke with the latest pending args (and cancel the timer)
 *   - cancel(): drop any pending invocation
 *
 * Used by the filter drawer's search box so typing doesn't fire a server
 * request per keystroke, while Apply can flush the pending value immediately.
 */
export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  flush(): void;
  cancel(): void;
}

export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  wait: number,
): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingArgs: A | null = null;

  const debounced = ((...args: A) => {
    pendingArgs = args;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const a = pendingArgs as A;
      pendingArgs = null;
      fn(...a);
    }, wait);
  }) as Debounced<A>;

  debounced.flush = () => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
    const a = pendingArgs as A;
    pendingArgs = null;
    fn(...a);
  };

  debounced.cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    pendingArgs = null;
  };

  return debounced;
}
